import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/Toaster';
import { useActivityHeartbeat } from '@/features/activity/hooks/useActivityHeartbeat';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { AppRoutes } from '@/routes';

/**
 * React Query handles every server-state fetch from here on (student lists,
 * attendance registers, fee dues). Auth stays in zustand because it is client
 * state that everything else depends on.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // The axios layer already handles token refresh; retrying a genuine
      // 401/403 just delays the redirect to /login.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  const bootstrap = useAuthStore((state) => state.bootstrap);

  // Runs once on load: swaps the httpOnly refresh cookie for a fresh access
  // token so a page reload does not sign the user out.
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Minutes in the app, for the platform dashboard. Does nothing until somebody
  // from an institute is signed in.
  useActivityHeartbeat();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
        {/* One host for every screen's confirmations and failures. */}
        <Toaster />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
